// src/components/Vendors/VendorDeleteModal.tsx
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Dialog } from '@components/common/Dialog';
import Button from '@components/common/Button';
import type { VendorSearchResponse } from '@schemas/api';

interface VendorDeleteModalProps {
  vendor: VendorSearchResponse | null;
  isLoading?: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export const VendorDeleteModal: React.FC<VendorDeleteModalProps> = ({
  vendor,
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Dialog
      open={!!vendor}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <AlertTriangle
            className="h-6 w-6 text-red-600 dark:text-red-400"
            aria-hidden="true"
          />
          <h2 className="text-lg font-semibold text-content-primary">
            Delete Vendor?
          </h2>
        </div>

        <p className="text-content-secondary">
          Are you sure you want to delete{' '}
          <strong>{vendor?.legal_name}</strong>? This action cannot be undone.
        </p>

        <div className="flex gap-3 pt-4" role="group" aria-label="Delete confirmation actions">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirm}
            disabled={isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};
